'use memo'

import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  REGISTRATION_ROLE_PARAM,
  REGISTRATION_TEAM_PARAM,
} from '@sportninja/common/features/registration/registrationUrlParams'
import { findDivisionTeamById } from '@sportninja/common/features/registration/startStep/divisionTeamIndex'
import { linkAutoAdvance } from '@sportninja/common/features/registration/teamLink/linkAutoAdvance'
import type { RegistrationTeam } from '@sportninja/common/hooks/useRegistration'
import type { Division } from '@sportninja/common/types'
import {
  committedSelection,
  type CommittedSelection,
} from '../utils/committedSelection'
import type { CommitOptions } from './useStartCommit'

/** Where the auto-advance is: waiting to fire, committing, or given up. */
type Phase = 'idle' | 'advancing' | 'failed'

interface Params {
  /** The registration payload's top-level teams — what `?team_id=` resolves against. */
  teams: RegistrationTeam[] | null | undefined
  /** The registration's own divisions: the canonical copy the commit hands on. */
  divisions: Division[]
  /** The team the stored session already holds. */
  sessionTeamId: string | null | undefined
  isLogged: boolean
  /** The player registration type's id; undefined until the types load. */
  playerTypeId: string | undefined
  /** Put the step on the player role — the step's own role handler. */
  selectPlayerRole: () => void
  commitAndAdvance: (
    committed: CommittedSelection<Division>,
    options: CommitOptions
  ) => Promise<boolean>
}

/**
 * Commit a `?team_id=` link on the reader's behalf and move them on.
 *
 * `linkAutoAdvance` decides when a link is a complete answer; this only fires
 * the start step's own commit, once per mount — a remount (Back from Sign Up)
 * decides afresh.
 *
 * `isAutoAdvancing` is true from the first render the commit can fire
 * until the step is left, so the view can show its skeleton instead of
 * controls that are about to be ignored. It is latched: the PUT's session
 * update and the link being dropped both re-render this step before it
 * navigates, and neither may flash the real step back in.
 */
export const useTeamLinkAutoAdvance = (
  params: Params
): { isAutoAdvancing: boolean } => {
  const { search } = useLocation()
  const query = new URLSearchParams(search)
  const entry = findDivisionTeamById(
    params.teams,
    query.get(REGISTRATION_TEAM_PARAM)
  )
  const decision = linkAutoAdvance({
    entry,
    roleParam: query.get(REGISTRATION_ROLE_PARAM),
    sessionTeamId: params.sessionTeamId,
    isLogged: params.isLogged,
  })
  const canFire = decision === 'advance' && Boolean(params.playerTypeId)

  const [phase, setPhase] = useState<Phase>('idle')
  const hasFired = useRef(false)
  // Kept current so the effect reads this render's values without listing
  // objects and functions rebuilt every render — the same pattern as
  // `usePreselectFromUrl`.
  const latest = useRef({ params, entry })
  latest.current = { params, entry }

  useEffect(() => {
    if (hasFired.current || !canFire) {
      return
    }
    const { params: current, entry: target } = latest.current
    if (!target) {
      return
    }
    hasFired.current = true
    setPhase('advancing')
    // The link implies a player. The step's own handler, so a genuine switch
    // (a reused rep session) clears the other role's leftovers as a click would.
    current.selectPlayerRole()
    // Committed straight from the resolved link, not from the step's local
    // selection: the pre-select and role effects are still settling, and the
    // skeleton hides them anyway. Same `committedSelection`, so the division
    // is still the canonical `registration.divisions` copy.
    current
      .commitAndAdvance(
        committedSelection({
          selectedDivision: null,
          selectedTeam: target,
          isTeamRole: false,
          divisions: current.divisions,
        }),
        {
          registrationTypeId: current.playerTypeId,
          roleKey: 'player',
          consumeLink: true,
        }
      )
      .then((advanced) => {
        if (!advanced) {
          setPhase('failed')
        }
      })
      .catch(() => setPhase('failed'))
  }, [canFire])

  return {
    isAutoAdvancing: (canFire && phase === 'idle') || phase === 'advancing',
  }
}
